<template>
  <div>
    <!-- Tela principal, só aparece quando câmera e localização OK -->
    <div v-if="camera.ready && location.status" class="capture-checkin">

      <!-- Se ainda não tirou foto, mostra o vídeo ao vivo -->
      <div v-if="!camera.data" class="camera-area">
        <div class="mb-3">
          <video class="w-100 rounded" ref="video" autoplay playsinline muted></video>
        </div>
        <button class="btn btn-default btn-block btn-lg" @click="takePhoto" :disabled="!camera.ready || !camera.active">
          Tirar Foto
        </button>
      </div>

      <!-- Depois da foto -->
      <div v-else class="photo-area">
        <div class="mb-3">
          <img class="w-100 rounded" :src="camera.data" alt="Foto capturada" />
        </div>

        <div>
          <button class="btn btn-warning btn-block btn-lg" @click="resetPhoto">
            Refazer
          </button>
          <button class="btn btn-success btn-block btn-lg" @click="sendPhoto" :disabled="send.loading">
            Enviar foto
            <b-spinner v-if="send.loading" small class="ml-2 align-middle" variant="white" role="status"></b-spinner>
          </button>
        </div>
      </div>
    </div>

    <!-- Tela de permissões -->
    <div v-else>
      <div v-if="!camera.ready">
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" style="fill: #31304F;">
          <path d="M12 8c-2.168 0-4 1.832-4 4s1.832 4 4 4 4-1.832 4-4-1.832-4-4-4zm0 6c-1.065 0-2-.935-2-2s.935-2 2-2 2 .935 2 2-.935 2-2 2z"></path>
          <path
            d="M20 5h-2.586l-2.707-2.707A.996.996 0 0 0 14 2h-4a.996.996 0 0 0-.707.293L6.586 5H4c-1.103 0-2 .897-2 2v11c0 1.103.897 2 2 2h16c1.103 0 2-.897 2-2V7c0-1.103-.897-2-2-2zM4 18V7h3c.266 0 .52-.105.707-.293L10.414 4h3.172l2.707 2.707A.996.996 0 0 0 17 7h3l.002 11H4z">
          </path>
        </svg>
        <h5 class="mt-2 mb-5">Você precisa permitir o acesso à sua câmera.</h5>
        <button class="btn btn-default btn-block btn-lg" @click="initCamera">Permitir câmera</button>
      </div>

      <div v-else-if="!location.status">
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" style="fill: #31304F;">
          <path d="M12 14c2.206 0 4-1.794 4-4s-1.794-4-4-4-4 1.794-4 4 1.794 4 4 4zm0-6c1.103 0 2 .897 2 2s-.897 2-2 2-2-.897-2-2 .897-2 2-2z"></path>
          <path
            d="M11.42 21.814a.998.998 0 0 0 1.16 0C12.884 21.599 20.029 16.44 20 10c0-4.411-3.589-8-8-8S4 5.589 4 9.995c-.029 6.445 7.116 11.604 7.42 11.819zM12 4c3.309 0 6 2.691 6 6.005.021 4.438-4.388 8.423-6 9.73-1.611-1.308-6.021-5.294-6-9.735 0-3.309 2.691-6 6-6z">
          </path>
        </svg>
        <h5 class="mt-2 mb-5">Você precisa permitir o acesso à sua localização.</h5>
        <button class="btn btn-default btn-block btn-lg" @click="checkLocation">Permitir localização</button>
      </div>
    </div>
  </div>
</template>

<script>
import { api } from "@/state/services";

export default {
  name: 'Camera',

  props: {
    inspection: {
      default: null
    },
    item: {
      default: null
    }
  },

  data() {
    return {
      camera: {
        stream: null,  // mediaStream da câmera
        ready: false,  // já tivemos permissão e ligamos a câmera pelo menos uma vez
        active: false, // stream ainda está ativa (não parada)
        data: null,    // base64 da foto
        send: false,   // payload pode ser enviado
      },

      location: {
        status: false,
        lat: null,
        lng: null,
      },

      send: {
        loading: false,
      }
    }
  },

  methods: {
    async initCamera() {
      try {
        // tenta acessar a câmera
        const newStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        })

        this.camera.stream = newStream
        this.camera.active = true
        this.camera.ready = true // já temos permissão

        const videoEl = this.$refs.video
        if (videoEl) {
          videoEl.srcObject = this.camera.stream

          // só depois que o vídeo realmente carregou, tentamos localização
          videoEl.onloadedmetadata = () => {
            // garante que está tocando
            if (videoEl.play) {
              videoEl.play().catch(() => { })
            }
            this.checkLocation()
          }
        } else {
          // sem referência de vídeo ainda (render atrasou?), tenta pedir localização mesmo assim
          this.checkLocation()
        }
      } catch (err) {
        console.error('Erro ao acessar câmera:', err)
        this.camera.ready = false
        this.camera.active = false
      }
    },

    attachStreamToVideo() {
      // só anexa quando o <video> realmente estiver renderizado
      this.$nextTick(() => {
        const videoEl = this.$refs.video
        if (!videoEl || !this.camera.stream) return

        // liga stream no elemento
        if (videoEl.srcObject !== this.camera.stream) {
          videoEl.srcObject = this.camera.stream
        }

        // garante autoplay (muted já está no template)
        const p = videoEl.play && videoEl.play()
        if (p && typeof p.then === 'function') {
          p.catch(() => {
            // silêncio caso o navegador ainda exija gesto do usuário
          })
        }
      })
    },

    takePhoto() {
      const videoEl = this.$refs.video
      if (!videoEl) return

      const canvas = document.createElement('canvas')
      const width = videoEl.videoWidth || 640
      const height = videoEl.videoHeight || 480
      canvas.width = width
      canvas.height = height

      const ctx = canvas.getContext('2d')
      ctx.drawImage(videoEl, 0, 0, width, height)

      this.camera.data = canvas.toDataURL('image/jpeg', 0.9)
      this.camera.send = true

      // agora que já capturou, podemos desligar a câmera
      this.stopCameraStream()
    },

    resetPhoto() {
      // limpa a foto e religa a câmera
      this.camera.data = null
      this.camera.send = false
      this.initCamera()
    },

    sendPhoto() {
      this.send.loading = true;

      api
        .post('vehicles/inspections', {
          inspection: this.inspection,
          item: this.item,
          photo: this.camera.data,
          geolocation: this.location.lat + ',' + this.location.lng,
        }).then(response => {
          if (response.data.status == 'success') {
            this.$toast.success('A foto foi enviada com sucesso.')
            this.resetPhoto()
            this.$parent.$parent.$parent.$parent.hideSend()
            this.$parent.$parent.$parent.$parent.getInspection()

            this.send.loading = false;
          } else {
            this.$toast.error(response.data.message)

            this.send.loading = false;
          }
        })
        .catch(error => {
          if (error) {
            this.send.loading = false;
          }
        })
        .finally(() => {
          this.send.loading = false;
        })
    },

    stopCameraStream() {
      if (this.camera.stream) {
        this.camera.stream.getTracks().forEach(track => track.stop())
      }
      this.camera.stream = null
      this.camera.active = false
    },

    checkLocation() {
      if (!navigator.permissions || !navigator.geolocation) {
        this.getLocation()
        return
      }

      navigator.permissions.query({ name: 'geolocation' })
        .then((result) => {
          if (result.state === 'granted' || result.state === 'prompt') {
            this.getLocation()
          } else {
            this.location.status = false
          }
        })
        .catch(() => {
          this.getLocation()
        })
    },

    getLocation() {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          this.location.status = true
          this.location.lat = pos.coords.latitude
          this.location.lng = pos.coords.longitude
          this.camera.send = true
        },
        (err) => {
          console.warn('Geolocation error', err)
          this.location.status = false
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      )
    },
  },

  mounted() {
    this.initCamera()
  },

  beforeDestroy() {
    this.stopCameraStream()
  },

  watch: {
    // quando a permissão de localização ficar true, o bloco com <video> pode aparecer
    'location.status'(val) {
      if (val) this.attachStreamToVideo()
    },
    // se a câmera ficar pronta depois, também reanexa
    'camera.ready'(val) {
      if (val) this.attachStreamToVideo()
    },
  },
}
</script>
